import { parseJsonBody } from "@/lib/general.utils";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import BlogDetailComponent from "@/components/Blogs/BlogDetailComponent";
import BlogDetailSkeleton from "@/skeletons/BlogDetailSkeleton";
import useGeneralStore from "@/store/General/useGeneralStore";

const BlogDetail = () => {
  const router = useRouter();
  const { id } = router.query;
  const { i18n, t } = useTranslation("common");
  const currentLang = i18n.language || "en";
  const {
    fetchBlogDetailComponent,
    fetchBlogsComponent,
    BlogDetailComponent: blog,
    isLoadingBlogDetailComponent,
  } = useGeneralStore((state) => ({
    fetchBlogDetailComponent: state.fetchBlogDetailComponent,
    fetchBlogsComponent: state.fetchBlogsComponent,
    BlogDetailComponent: state.BlogDetailComponent,
    isLoadingBlogDetailComponent: state.isLoadingBlogDetailComponent,
  }));

  useEffect(() => {
    if (id) {
      if (blog?.id !== Number(id) || blog?.lang !== currentLang) {
        fetchBlogDetailComponent(parseInt(id as string));
      }
      // Also fetch blogs for recommendations
      fetchBlogsComponent();
    }
  }, [id, fetchBlogDetailComponent, fetchBlogsComponent, currentLang]);

  const translatedId =
    blog && blog.id === Number(id)
      ? parseJsonBody(blog.jsonBody).translationIds?.[currentLang]
      : undefined;
  useEffect(() => {
    if (translatedId && Number(translatedId) !== Number(id)) {
      void router.replace(`/blogs/${translatedId}`, undefined, {
        locale: currentLang,
      });
    }
  }, [translatedId, id, currentLang, router]);

  // Show skeleton while loading
  if (
    isLoadingBlogDetailComponent ||
    (translatedId && Number(translatedId) !== Number(id))
  ) {
    return <BlogDetailSkeleton />;
  }

  // Handle blog not found
  if (!isLoadingBlogDetailComponent && id && !blog && router.isReady) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t("blogsPage.noBlogsFound")}
          </h1>
          <button
            onClick={() => router.push("/blogs")}
            className="text-primary hover:underline"
          >
            {t("blogsPage.backToBlogs")}
          </button>
        </div>
      </div>
    );
  }

  return <BlogDetailComponent />;
};

export default BlogDetail;
